/**
 * cart-progress.js
 * Reemplazo casero de CornerCart para Bijou.
 *
 * Requiere:
 *  - snippet cart-progress-bar.liquid renderizado dentro del cart-drawer
 *  - una colección en Shopify con handle "regalos" (o el que definas en el data-attribute)
 *  - descuentos automáticos configurados en Shopify Admin (envío gratis, buy-x-get-y, mayoreo)
 *
 * El precio de envío gratis / regalo / mayoreo lo resuelve Shopify en checkout mediante
 * los descuentos automáticos. Este script SOLO calcula el progreso visual y gestiona
 * la selección del regalo. No modifica precios.
 */

(function () {
  const GIFT_PROPERTY_KEY = '_bijou_gift';
  const GIFT_PROMPTED_KEY = 'bijou_gift_prompted';
  const DRAWER_OPEN_CLASS = 'bijou-cart-drawer-open';

  let previousGiftReached = null;
  let drawerWasOpen = false;
  let lastKnownState = { giftReached: false, hasGift: false };

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  // ---- Control de "ya le ofrecimos el regalo en esta sesión" ----

  function hasBeenPrompted() {
    try {
      return sessionStorage.getItem(GIFT_PROMPTED_KEY) === 'true';
    } catch (e) {
      return false;
    }
  }

  function markPrompted() {
    try {
      sessionStorage.setItem(GIFT_PROMPTED_KEY, 'true');
    } catch (e) {
      /* modo privado o storage bloqueado */
    }
  }

  // ---- Bloqueo de scroll ----

  function lockScroll() {
    document.body.classList.add('gift-picker-open');
  }

  function unlockScroll() {
    document.body.classList.remove('gift-picker-open');
  }

  function clearStaleLock() {
    const modal = qs('[data-gift-picker-modal]');
    if (!modal || modal.hidden) unlockScroll();
  }

  function isCartDrawerOpen() {
    if (document.body.classList.contains(DRAWER_OPEN_CLASS)) return true;
    const drawer = qs('cart-drawer');
    if (drawer && drawer.classList.contains('active')) return true;
    return false;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function addGiftToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [GIFT_PROPERTY_KEY]: 'true' },
          },
        ],
      }),
    }).then((r) => r.json());
  }

  async function removeCartLine(lineIndex) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: 0 }),
    }).then((r) => r.json());
  }

  function findExistingGiftLine(cart) {
    const idx = cart.items.findIndex(
      (item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true'
    );
    return idx === -1 ? null : idx + 1;
  }

  function getRelevantCartMetrics(cart) {
    let subtotal = 0;
    let itemCount = 0;

    cart.items.forEach((item) => {
      const isGift = item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true';
      if (isGift) return;

      subtotal += item.final_line_price != null ? item.final_line_price : item.line_price;
      itemCount += item.quantity;
    });

    return { subtotal, itemCount };
  }

  function computeFillPercent(milestoneEls, nextMilestone) {
    const container = milestoneEls.length ? milestoneEls[0].parentElement : null;
    if (!container) return 0;
    const containerWidth = container.offsetWidth;
    if (!containerWidth) return 0;

    function centerX(el) {
      return el.offsetLeft + el.offsetWidth / 2;
    }

    if (!nextMilestone) {
      const last = milestoneEls[milestoneEls.length - 1];
      return (centerX(last) / containerWidth) * 100;
    }

    const nextIndex = milestoneEls.indexOf(nextMilestone.el);
    const nextX = centerX(nextMilestone.el);
    const prevX = nextIndex > 0 ? centerX(milestoneEls[nextIndex - 1]) : 0;
    const progress = Math.min(nextMilestone.currentVal / nextMilestone.targetVal, 1);
    const fillX = prevX + (nextX - prevX) * progress;
    return (fillX / containerWidth) * 100;
  }

  // ---- Link de regalo garantizado ----
  // El botón original vive dentro de la fila del producto de regalo, así que
  // no existe mientras el cliente no haya elegido. Inyectamos un link discreto
  // en el bloque de progreso para que la oferta sea visible sin robar espacio.
  function ensureGiftButton(root) {
    const nativo = qs('[data-gift-select-btn]:not([data-bijou-injected])');
    const inyectado = qs('[data-gift-select-btn][data-bijou-injected]');

    if (nativo) {
      if (inyectado) {
        const w = inyectado.closest('[data-bijou-gift-cta-wrap]');
        if (w) w.remove();
        else inyectado.remove();
      }
      return nativo;
    }
    if (inyectado) return inyectado;
    if (!root) return null;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bijou-gift-cta';
    btn.setAttribute('data-gift-select-btn', '');
    btn.setAttribute('data-bijou-injected', 'true');
    btn.innerHTML = '<span data-gift-select-label>Elige tu regalo</span>';
    btn.style.cssText = [
      'display:inline',
      'margin:0',
      'padding:2px 0',
      'border:0',
      'border-radius:0',
      'background:transparent',
      'color:#9d6c4f',
      'font-size:12px',
      'font-weight:500',
      'line-height:1.3',
      'text-decoration:underline',
      'text-underline-offset:2px',
      'white-space:nowrap',
      'cursor:pointer',
      'width:auto',
      'min-width:0',
      'min-height:0',
      'height:auto',
      'box-shadow:none',
      'letter-spacing:0',
      'text-transform:none',
      'opacity:.85',
    ].join(';');

    const wrap = document.createElement('div');
    wrap.setAttribute('data-bijou-gift-cta-wrap', '');
    wrap.style.cssText = 'display:flex;justify-content:flex-end;width:100%;margin-top:4px';
    wrap.appendChild(btn);

    root.appendChild(wrap);
    return btn;
  }

  function renderMilestones(root, cart) {
    const milestoneEls = qsa('.cart-milestone', root);
    const { subtotal, itemCount } = getRelevantCartMetrics(cart);

    let nextMilestone = null;

    milestoneEls.forEach((el) => {
      const type = el.dataset.milestoneType;
      const value = parseFloat(el.dataset.milestoneValue);
      const targetVal = type === 'money' ? value * 100 : value;
      const currentVal = type === 'money' ? subtotal : itemCount;
      const reached = currentVal >= targetVal;

      el.classList.toggle('is-reached', reached);
      const iconEl = qs('.cart-milestone__icon', el);
      if (iconEl) {
        iconEl.textContent = reached ? '✓' : iconEl.dataset.icon;
      }

      if (!reached && !nextMilestone) {
        nextMilestone = { el, type, currentVal, targetVal };
      }
    });

    const fillPct = computeFillPercent(milestoneEls, nextMilestone);

    const fill = qs('[data-milestones-fill]', root);
    if (fill) fill.style.width = fillPct + '%';

    const message = qs('[data-progress-message]', root);
    if (message) {
      if (!nextMilestone) {
        message.textContent = '¡Desbloqueaste todos los beneficios! 🎉';
      } else {
        const label = qs('.cart-milestone__label', nextMilestone.el);
        const labelText = label ? label.textContent.trim() : '';
        const remaining = nextMilestone.targetVal - nextMilestone.currentVal;
        if (nextMilestone.type === 'money') {
          message.textContent = `Agrega ${formatMoney(remaining)} más para ${labelText.toLowerCase()}`;
        } else {
          message.textContent = `Agrega ${remaining} pieza(s) más para desbloquear ${labelText}`;
        }
      }
    }

    const giftThresholdRaw = parseInt(root.dataset.giftThreshold, 10);
    const giftThreshold = isNaN(giftThresholdRaw) ? Infinity : giftThresholdRaw * 100;
    const giftReached = subtotal >= giftThreshold;
    const existingGiftLine = findExistingGiftLine(cart);

    lastKnownState = { giftReached, hasGift: !!existingGiftLine };

    const giftBtn = ensureGiftButton(root);
    if (giftBtn) {
      giftBtn.hidden = !giftReached;
      const wrap = giftBtn.closest('[data-bijou-gift-cta-wrap]');
      if (wrap) wrap.hidden = !giftReached;
      const label = qs('[data-gift-select-label]', giftBtn);
      if (label) {
        label.textContent = existingGiftLine ? 'Cambiar tu regalo' : 'Elige tu regalo';
      }
    }

    const modal = qs('[data-gift-picker-modal]');
    const modalOpen = modal && !modal.hidden;

    // Auto-apertura por cruce real de umbral (el cliente acaba de agregar algo
    // que lo hizo elegible) y solo con el carrito abierto.
    const esCruceReal = previousGiftReached === false && giftReached;
    previousGiftReached = giftReached;

    if (esCruceReal && !existingGiftLine && !modalOpen && !hasBeenPrompted() && isCartDrawerOpen()) {
      markPrompted();
      openGiftPicker(root);
    }
  }

  // ---- Auto-apertura al abrir el carrito ----
  // Es acción del cliente, no de la carga de página, así que no bloquea la PDP.
  function initDrawerOpenWatcher() {
    if (document.body.dataset.bijouDrawerWatch) return;
    document.body.dataset.bijouDrawerWatch = 'true';

    drawerWasOpen = isCartDrawerOpen();

    const observer = new MutationObserver(() => {
      const abierto = isCartDrawerOpen();
      if (abierto && !drawerWasOpen) {
        drawerWasOpen = true;
        setTimeout(maybeOpenOnDrawer, 400);
      } else if (!abierto) {
        drawerWasOpen = false;
      }
    });

    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  async function maybeOpenOnDrawer() {
    if (hasBeenPrompted()) return;
    if (!isCartDrawerOpen()) return;

    const modal = qs('[data-gift-picker-modal]');
    if (modal && !modal.hidden) return;

    const root = qs('[data-cart-progress]');
    if (!root) return;

    const cart = await fetchCart();
    const { subtotal } = getRelevantCartMetrics(cart);
    const giftThresholdRaw = parseInt(root.dataset.giftThreshold, 10);
    const giftThreshold = isNaN(giftThresholdRaw) ? Infinity : giftThresholdRaw * 100;

    if (subtotal < giftThreshold) return;
    if (findExistingGiftLine(cart)) return;

    markPrompted();
    openGiftPicker(root);
  }

  function initMilestonesNav(root) {
    const track = qs('[data-milestones-track]', root);
    if (!track || track.dataset.navInit) return;
    track.dataset.navInit = 'true';

    const prevBtn = qs('[data-milestones-prev]', root);
    const nextBtn = qs('[data-milestones-next]', root);

    function updateNavVisibility() {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (prevBtn) prevBtn.hidden = track.scrollLeft <= 12;
      if (nextBtn) nextBtn.hidden = maxScroll <= 12 || track.scrollLeft >= maxScroll - 50;
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
      });
    }
    track.addEventListener('scroll', updateNavVisibility);
    window.addEventListener('resize', updateNavVisibility);
    updateNavVisibility();
  }

  function initGiftModalObserver() {
    const modal = qs('[data-gift-picker-modal]');
    if (!modal || modal.dataset.bijouObserverInit) return;
    modal.dataset.bijouObserverInit = 'true';

    const observer = new MutationObserver(() => {
      if (modal.hidden) unlockScroll();
    });

    observer.observe(modal, { attributes: true, attributeFilter: ['hidden'] });
  }

  let isSelfUpdating = false;

  async function renderAll() {
    const root = qs('[data-cart-progress]');
    if (window.BijouCartAddons && window.BijouCartAddons.initPromoBanner) {
      window.BijouCartAddons.initPromoBanner();
    }
    initGiftModalObserver();
    initDrawerOpenWatcher();
    clearStaleLock();
    if (!root) return;
    const cart = await fetchCart();
    isSelfUpdating = true;
    renderMilestones(root, cart);
    initMilestonesNav(root);
    requestAnimationFrame(() => {
      isSelfUpdating = false;
    });
    return cart;
  }

  function initCartObserver() {
    const target = document.getElementById('CartDrawer');
    if (!target) return;

    let debounceTimer;
    const observer = new MutationObserver(() => {
      if (isSelfUpdating) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(renderAll, 150);
    });

    observer.observe(target, { childList: true, subtree: true });
  }

  // ---- Gift picker modal ----

  async function fetchGiftProducts(handle) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(`/collections/${handle}/products.json?limit=20`, {
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(`No se pudo cargar la colección (status ${res.status})`);
      }
      const data = await res.json();
      return data.products || [];
    } finally {
      clearTimeout(timeoutId);
    }
  }

  function buildGiftRow(product) {
    const hasMultipleVariants = product.variants.length > 1;
    const defaultVariant = product.variants.find((v) => v.available) || product.variants[0];
    const image = product.images && product.images[0] ? product.images[0].src : '';
    const compareAt = defaultVariant.compare_at_price;

    const row = document.createElement('div');
    row.className = 'gift-picker-row';

    let sizeSelectorHTML = '';
    if (hasMultipleVariants) {
      const optionsHTML = product.variants
        .map(
          (v) =>
            `<option value="${v.id}" ${!v.available ? 'disabled' : ''} ${
              v.id === defaultVariant.id ? 'selected' : ''
            }>${v.title}${!v.available ? ' (agotado)' : ''}</option>`
        )
        .join('');
      sizeSelectorHTML = `
        <select class="gift-picker-row-size" data-gift-size-select>
          ${optionsHTML}
        </select>
      `;
    }

    row.innerHTML = `
      <img src="${image}" alt="${product.title}" loading="lazy" width="56" height="56">
      <span class="gift-picker-row-info">
        <span class="gift-picker-row-title">${product.title}</span>
        ${sizeSelectorHTML}
        <span class="gift-picker-row-price">
          ${compareAt ? `<s>${formatMoney(compareAt * 100)}</s>` : ''}
          <span class="gift-picker-badge">GRATIS</span>
        </span>
      </span>
      <input type="radio" name="bijou-gift-selection" class="gift-picker-row-radio" value="${defaultVariant.id}" ${!defaultVariant.available ? 'disabled' : ''}>
      <span class="gift-picker-row-check" aria-hidden="true"></span>
    `;

    if (!defaultVariant.available) {
      row.classList.add('is-unavailable');
    }

    return row;
  }

  async function openGiftPicker(root) {
    const target = root || qs('[data-cart-progress]');
    const modal = qs('[data-gift-picker-modal]');
    const grid = modal ? qs('[data-gift-picker-grid]', modal) : null;
    const confirmBtn = modal ? qs('[data-gift-picker-confirm]', modal) : null;

    if (!target || !modal || !grid) {
      console.error('[BijouCart] No se encontró el modal de regalo o su contenedor de progreso.');
      return;
    }

    const handle = target.dataset.giftCollection;

    modal.hidden = false;
    lockScroll();
    markPrompted();
    grid.innerHTML = '<p class="gift-picker-loading">Cargando opciones…</p>';
    if (confirmBtn) confirmBtn.disabled = true;

    try {
      const products = await fetchGiftProducts(handle);
      grid.innerHTML = '';
      if (!products.length) {
        grid.innerHTML = '<p>No hay regalos disponibles en este momento.</p>';
        return;
      }
      products.forEach((p) => grid.appendChild(buildGiftRow(p)));
    } catch (e) {
      console.error('[BijouCart] Error cargando regalos:', e);
      grid.innerHTML = '<p>Ocurrió un error al cargar los regalos. Cierra este panel e intenta de nuevo.</p>';
    }
  }

  function closeGiftPicker() {
    const modal = qs('[data-gift-picker-modal]');
    if (modal) modal.hidden = true;
    unlockScroll();
  }

  async function refreshCartDrawerHTML() {
    try {
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      if (!response.ok) return;
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const freshCartDrawer = doc.getElementById('CartDrawer');
      const currentCartDrawer = document.getElementById('CartDrawer');
      if (freshCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = freshCartDrawer.innerHTML;
      }
    } catch (e) {
      console.error('[BijouCart] No se pudo refrescar el contenido del carrito:', e);
    }
  }

  async function selectGift(variantId) {
    try {
      const cart = await fetchCart();
      const existingLine = findExistingGiftLine(cart);
      if (existingLine) {
        await removeCartLine(existingLine);
      }
      await addGiftToCart(variantId);
      await refreshCartDrawerHTML();
    } catch (e) {
      console.error('[BijouCart] Error al seleccionar el regalo:', e);
    } finally {
      closeGiftPicker();
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    await renderAll();
  }

  document.addEventListener('click', function (e) {
    const giftBtn = e.target.closest('[data-gift-select-btn]');
    if (giftBtn) {
      e.preventDefault();
      openGiftPicker(qs('[data-cart-progress]'));
      return;
    }

    if (e.target.closest('[data-gift-picker-close]') || e.target.closest('[data-gift-picker-skip]')) {
      markPrompted();
      closeGiftPicker();
      return;
    }

    const confirmBtn = e.target.closest('[data-gift-picker-confirm]');
    if (confirmBtn && !confirmBtn.disabled) {
      const modal = qs('[data-gift-picker-modal]');
      const checked = modal ? qs('input[name="bijou-gift-selection"]:checked', modal) : null;
      if (checked) {
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Agregando…';
        selectGift(checked.value);
      }
      return;
    }

    const giftRow = e.target.closest('.gift-picker-row');
    if (giftRow && !e.target.closest('[data-gift-size-select]')) {
      const radio = qs('.gift-picker-row-radio', giftRow);
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-gift-size-select]')) {
      const row = e.target.closest('.gift-picker-row');
      const radio = row ? qs('.gift-picker-row-radio', row) : null;
      if (radio) {
        radio.value = e.target.value;
        radio.checked = true;
      }
      const modal = qs('[data-gift-picker-modal]');
      const confirmBtn = modal ? qs('[data-gift-picker-confirm]', modal) : null;
      if (confirmBtn) confirmBtn.disabled = false;
      return;
    }

    if (e.target.name === 'bijou-gift-selection') {
      const modal = qs('[data-gift-picker-modal]');
      const confirmBtn = modal ? qs('[data-gift-picker-confirm]', modal) : null;
      if (confirmBtn) confirmBtn.disabled = false;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    const modal = qs('[data-gift-picker-modal]');
    if (modal && !modal.hidden) {
      markPrompted();
      closeGiftPicker();
    }
  });

  document.addEventListener('cart:updated', renderAll);
  document.addEventListener('cart:refresh', renderAll);

  onReady(renderAll);
  onReady(initCartObserver);
  onReady(initDrawerOpenWatcher);
  onReady(clearStaleLock);

  window.addEventListener('pageshow', clearStaleLock);

  window.BijouCartProgress = {
    renderAll,
    openGiftPicker,
    closeGiftPicker,
    debug: function () {
      const root = qs('[data-cart-progress]');
      console.log({
        rootEncontrado: !!root,
        umbral: root ? root.dataset.giftThreshold : null,
        coleccion: root ? root.dataset.giftCollection : null,
        modalEncontrado: !!qs('[data-gift-picker-modal]'),
        botonEncontrado: !!qs('[data-gift-select-btn]'),
        drawerAbierto: isCartDrawerOpen(),
        yaOfrecido: hasBeenPrompted(),
        estado: lastKnownState,
      });
    },
  };
})();